import type { RequestFn } from '../http';

export type LlmProvider = 'openai' | 'vertex';

export interface LlmConfigStatus {
  source: 'db' | 'env' | 'none';
  provider: LlmProvider | null;
  openai?: { baseUrl: string; model?: string; apiKeySet: boolean };
  vertex?: { region: string; model?: string; serviceAccountSet: boolean; projectId?: string };
}

export type SaveLlmConfigBody =
  | { provider: 'openai'; apiKey: string; baseUrl?: string; model?: string }
  | { provider: 'vertex'; serviceAccountJson: string; region: string; model?: string };

/** Prozess-lokale Guardrail-Zähler (Backend GET /settings/kian) */
export interface KianGuardrailMetricsSnapshot {
  credentialHitsOutput: number;
  credentialHitsUserInput: number;
  argInjectionBlocks: number;
  argCredentialBlocks: number;
  pseudonymOverflows: number;
}

export interface KianConfig {
  toolsEnabled: boolean;
  piiRedactionEnabled: boolean;
  piiDetectionMode: 'regex' | 'presidio' | 'presidio+llm';
  injectionDetectionMode: 'off' | 'regex' | 'regex+ml';
  maxToolCallsPerMessage: number;
  metrics?: KianGuardrailMetricsSnapshot;
}

export type SaveKianConfigBody = Partial<{
  toolsEnabled: boolean;
  piiRedactionEnabled: boolean;
  piiDetectionMode: KianConfig['piiDetectionMode'];
  injectionDetectionMode: KianConfig['injectionDetectionMode'];
  maxToolCallsPerMessage: number;
}>;

export function createSettingsApi(request: RequestFn) {
  return {
    // ── Neue strukturierte LLM-Config-API ───────────────────────────────────

    async getLlmConfig() {
      return request<LlmConfigStatus>('/settings/llm-config');
    },

    async saveLlmConfig(body: SaveLlmConfigBody) {
      return request<{ success: boolean }>('/settings/save-llm-config', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },

    async deleteLlmConfig() {
      return request<{ success: boolean }>('/settings/llm-config', { method: 'DELETE' });
    },

    async testLlm() {
      return request<{
        success: boolean;
        message?: string;
        error?: string;
        dual?: {
          fast: { ok: boolean; model: string; latencyMs: number; error?: string };
          deep: { ok: boolean; model: string; latencyMs: number; error?: string };
        };
      }>(
        '/settings/test-llm',
        { method: 'POST', body: '{}' }
      );
    },

    // ── Modell-Liste ─────────────────────────────────────────────────────────

    async getAiModels() {
      return request<{ data: Array<{ id: string }> }>('/ai/models');
    },

    // ── Legacy-API (rückwärtskompatibel) ─────────────────────────────────────

    async getLlmKeyStatus() {
      return request<{ hasKey: boolean }>('/settings/llm-key-status');
    },

    async saveLlmKey(key: string) {
      return request<{ success: boolean }>('/settings/save-llm-key', {
        method: 'POST',
        body: JSON.stringify({ key }),
      });
    },

    async deleteLlmKey() {
      return request<{ success: boolean }>('/settings/llm-key', { method: 'DELETE' });
    },

    async testLlmKey() {
      return request<{ success: boolean; message?: string; error?: string }>(
        '/settings/test-llm-key',
        { method: 'POST', body: '{}' }
      );
    },

    /** Admin: Tool-Use / Guardrails (kollidiert nicht mit Assistant-Flyout `getKianConfig`) */
    async getKianAdminConfig() {
      return request<KianConfig>('/settings/kian');
    },

    async saveKianAdminConfig(body: SaveKianConfigBody) {
      return request<{ success: boolean }>('/settings/kian', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },
  };
}
